// EnvLogo.jsx
import { Box } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnv } from "../../hooks/useEnv";

// Logo imports
import EastViewLogo from "../../assets/logos/eastview-logo.png";
import lendmarqLogo from "../../assets/logos/lendmarq-logo.png";
import resfinLogo from "../../assets/logos/resfin-logo.png";
import resilenderLogo from "../../assets/logos/resilender-logo.png";
import handsomeHomebuyerLogo from "../../assets/logos/handsome-homebuyer-logo.png";

// Configuration object for logos - easy to add new ones
const LOGO_CONFIG = {
  resilender: {
    src: resilenderLogo,
    alt: "Resilender",
    hookEnvKey: "isResilender",
  },
  lendmarq: {
    src: lendmarqLogo,
    alt: "Lendmarq",
    hookEnvKey: "isLendmarq", // Update this to match your actual env key
  },
  eastview: {
    src: EastViewLogo,
    alt: "EastView",
    hookEnvKey: "isEastview",
  },
  resfin: {
    src: resfinLogo,
    alt: "Resfin",
    hookEnvKey: "isResfin",
  },
  handsomeHomebuyer: {
    src: handsomeHomebuyerLogo,
    alt: "Handsome Homebuyer",
    hookEnvKey: "isHandsomeHomebuyer",
  },
};

const EnvLogo = ({
  sx,
  maxHeight = 105,
  maxWidth = 145,
  testMode = false, // Toggle between test and production mode
  onClick, // Allow custom click handler
  type,
}) => {
  const [testIndex, setTestIndex] = useState(0);
  const navigate = useNavigate();
  const env = useEnv();

  const styles = useMemo(
    () => ({
      cursor: "pointer",
      display: "inline-block",
      lineHeight: 0,
      "& img": {
        display: "block",
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        width: "auto",
        height: "auto",
      },
      ...sx,
    }),
    [sx, maxHeight, maxWidth],
  );

  const handleClick = useCallback(() => {
    if (testMode) {
      setTestIndex((prev) => (prev + 1) % Object.keys(LOGO_CONFIG).length);
    } else if (onClick) {
      onClick();
    } else {
      navigate("/");
    }
  }, [testMode, onClick, navigate]);

  // Determine which logo to show
  const getCurrentLogo = useCallback(() => {
    // return LOGO_CONFIG["handsomeHomebuyer"];
    if (type) {
      return LOGO_CONFIG[type];
    }
    if (testMode) {
      // Test mode: cycle through all logos
      const logoKeys = Object.keys(LOGO_CONFIG);
      const currentKey = logoKeys[testIndex];
      return LOGO_CONFIG[currentKey];
    }

    // Production mode: check environment flags
    for (const [, config] of Object.entries(LOGO_CONFIG)) {
      if (env[config.hookEnvKey]) {
        return config;
      }
    }

    // Default fallback (optional - you can return null or a default logo)
    return null;
  }, [testMode, testIndex, type, env]);

  const currentLogo = getCurrentLogo();

  if (!currentLogo) {
    return null;
  }

  return (
    <Box role="button" onClick={handleClick} sx={styles}>
      <img src={currentLogo.src} alt={currentLogo.alt} />
    </Box>
  );
};

export default EnvLogo;
